import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useClassName } from 'common/hooks';
import { Checkbox } from 'public/components';
import DateRangePicker from './DateRangePicker';
import './styles/event-filters.less';

const TIME_OPTIONS = [
  { label: 'Morning', value: 'morning', description: 'Starts before 12 PM' },
  { label: 'Afternoon', value: 'afternoon', description: 'Starts after 12 PM' },
  { label: 'Evening', value: 'evening', description: 'Starts after 5 PM' },
];

const DATE_OPTIONS = [
  { label: 'Today', value: 'today' },
  { label: 'This Week', value: 'week' },
  { label: 'Custom Date', value: 'custom' },
];

const EventFilters = ({
  events,
  filters,
  onCategoryToggle,
  onDateRangeChange,
  onCustomDateChange,
  onTimeOfDayToggle,
  onResetFilters,
  variant = 'desktop',
  onApplyMobileFilters = null,
}) => {
  const classNames = useClassName('EventFilters');
  const [showAllCategories, setShowAllCategories] = useState(false);

  const scrollToEventsSection = () => {
    const eventsContainer = document.querySelector(
      '.general-events__events-container'
    );
    if (eventsContainer) {
      const headerOffset = variant === 'desktop' ? 100 : 150;
      const elementPosition = eventsContainer.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  const handleCategoryToggle = (category) => {
    onCategoryToggle(category);
    setTimeout(scrollToEventsSection, 50);
  };

  const handleDateRangeChange = (value) => {
    onDateRangeChange(value);
    setTimeout(scrollToEventsSection, 50);
  };

  const handleCustomDateChange = (ranges) => {
    onCustomDateChange({
      startDate: ranges.selection.startDate,
      endDate: ranges.selection.endDate,
    });
    setTimeout(scrollToEventsSection, 50);
  };

  const handleTimeOfDayToggle = (value) => {
    onTimeOfDayToggle(value);
    setTimeout(scrollToEventsSection, 50);
  };

  const handleResetFilters = () => {
    onResetFilters();
    setTimeout(scrollToEventsSection, 50);
  };

  const getCategories = () => {
    const categorySet = new Set();
    events?.forEach((event) => {
      event.categories?.forEach((category) => {
        if (category?.name && category?.name !== 'Events') {
          categorySet.add(category.name);
        }
      });
    });
    return Array.from(categorySet);
  };

  const categories = getCategories();
  const initialCategories = showAllCategories
    ? categories
    : categories.slice(0, 7);

  return (
    <div className={classNames(['content', variant])}>
      {/* Categories Section */}
      {categories.length > 0 && (
        <div className={classNames('category-section')}>
          <div className={classNames('category-header')}>Categories</div>
          {initialCategories.map((category) => (
            <label key={category} className={classNames('category-option')}>
              <Checkbox
                checked={filters.categories.has(category)}
                onChange={() => handleCategoryToggle(category)}
              />
              <span className={classNames('category-option-label')}>
                {category}
              </span>
            </label>
          ))}
          {categories.length > 7 && (
            <button
              onClick={() => setShowAllCategories(!showAllCategories)}
              className={classNames('show-more')}>
              {showAllCategories ? 'Show Less' : 'Show More'}
            </button>
          )}
        </div>
      )}

      {/* Date Section */}
      <div className={classNames('category-section')}>
        <div className={classNames('category-header')}>Date</div>
        {DATE_OPTIONS.map((option) => (
          <label key={option.value} className={classNames('category-option')}>
            <input
              type="checkbox"
              name="date"
              value={option.value}
              checked={filters.dateRange === option.value}
              onChange={() =>
                handleDateRangeChange(
                  filters.dateRange === option.value ? '' : option.value
                )
              }
              className={classNames('category-radio')}
            />
            <span className={classNames('category-option-label')}>
              {option.label}
            </span>
          </label>
        ))}

        {/* DateRange Picker */}
        {filters.dateRange === 'custom' && (
          <DateRangePicker
            filters={filters}
            onDateChange={handleCustomDateChange}
          />
        )}
      </div>

      {/* Time of Day Section */}
      <div className={classNames('category-section')}>
        <div className={classNames('category-header')}>Time of day</div>
        {TIME_OPTIONS.map((option) => (
          <label key={option.value} className={classNames('category-option')}>
            <Checkbox
              checked={filters.timeOfDay.has(option.value)}
              onChange={() => handleTimeOfDayToggle(option.value)}
            />
            <div className={classNames('times')}>
              <span className={classNames('category-option-label')}>
                {option.label}
              </span>
              <span className={classNames('start-time')}>
                {option.description}
              </span>
            </div>
          </label>
        ))}
      </div>

      {/* Reset Button */}
      <div className={classNames('buttons-container')}>
        <button
          onClick={handleResetFilters}
          className={classNames('reset-button')}>
          Reset All Filters
        </button>
        {variant === 'mobile' && (
          <button
            onClick={() => {
              setTimeout(scrollToEventsSection, 50);
              if (onApplyMobileFilters) onApplyMobileFilters();
            }}
            className={classNames('show-results-button')}>
            Show Results
          </button>
        )}
      </div>
    </div>
  );
};

EventFilters.propTypes = {
  events: PropTypes.array,
  filters: PropTypes.object,
  onCategoryToggle: PropTypes.func,
  onDateRangeChange: PropTypes.func,
  onCustomDateChange: PropTypes.func,
  onTimeOfDayToggle: PropTypes.func,
  onResetFilters: PropTypes.func,
  variant: PropTypes.oneOf(['desktop', 'mobile']),
  onApplyMobileFilters: PropTypes.func,
};

export default EventFilters;
